import React, { useEffect, useMemo, useRef } from "react";
import { HiTrash, HiLockClosed } from "react-icons/hi2";
import { HiCheck } from "react-icons/hi";
import { ActivityType } from "../../../types/activity";
import { PerformanceLog } from "../../../types/post";
import ActivityResultEditor, {
  initialVariablesFor,
} from "./ActivityResultEditor";
import { ResultRowState } from "./performanceDraft";
import { formatMeasurementWithUnit } from "../../../utils/unitUtils";
import { evaluatePersonalBest } from "../../../utils/personalBestPredictor";

// Colour for a 1–10 performance rating (red → yellow → green), as in
// AddPerformanceLogForm.
const RATING_COLORS = [
  "#dc2626",
  "#ef4444",
  "#f87171",
  "#fca5a5",
  "#fde68a",
  "#d9f99d",
  "#a7f3d0",
  "#6ee7b7",
  "#4ade80",
  "#22c55e",
];
const ratingColor = (value: number) => RATING_COLORS[value - 1] || "#22c55e";
const ratingBackground = (value: number) => {
  const percent = ((value - 1) / 9) * 100;
  const color = ratingColor(value);
  const track = "rgba(120,120,120,0.22)";
  return `linear-gradient(to right, ${color} 0%, ${color} ${percent}%, ${track} ${percent}%, ${track} 100%)`;
};

const Pill: React.FC<{
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}> = ({ active, onClick, children }) => (
  <button
    type="button"
    onClick={onClick}
    aria-pressed={active}
    className={`inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs font-bold border transition-colors ${
      active
        ? "bg-neutral text-neutral-content border-neutral"
        : "bg-base-100 text-base-content/60 border-base-300 hover:border-base-content/40"
    }`}
  >
    {active && <HiCheck className="w-3 h-3" />}
    {children}
  </button>
);

interface ResultRowEditorProps {
  row: ResultRowState;
  index: number;
  activityTypes: ActivityType[];
  loadingActivityTypes: boolean;
  onChange: (next: ResultRowState) => void;
  onRemove: () => void;
  onActivityCreated: (activityTypeId: string) => Promise<ActivityType | null>;
  /** Premium PB predictor history. Omitted / not ready → no auto-PB. */
  isPremium?: boolean;
  pastPerformanceLogs?: (PerformanceLog & { id: string })[];
  historyReady?: boolean;
  historyLoadFailed?: boolean;
}

/** One result row of step 3: activity + values, target vs actual, pills. */
const ResultRowEditor: React.FC<ResultRowEditorProps> = ({
  row,
  index,
  activityTypes,
  loadingActivityTypes,
  onChange,
  onRemove,
  onActivityCreated,
  isPremium = false,
  pastPerformanceLogs = [],
  historyReady = false,
  historyLoadFailed = false,
}) => {
  const hasMeasurementToEvaluate =
    !!row.activity?.id &&
    row.variables.some(
      (v, i) => row.selectedIndices.has(i) && v.measurement != null,
    );

  const personalBestEvaluation = useMemo(() => {
    if (
      !isPremium ||
      !row.activity?.id ||
      !historyReady ||
      historyLoadFailed
    ) {
      return null;
    }
    const primaryIndex = row.variables.findIndex(
      (v, i) => row.selectedIndices.has(i) && v.measurement != null,
    );
    if (primaryIndex === -1) return null;
    const primaryVariable = row.variables[primaryIndex];
    if (primaryVariable.measurement == null) return null;

    return {
      variableName: primaryVariable.name,
      ...evaluatePersonalBest(
        pastPerformanceLogs,
        row.activity.id,
        primaryVariable.name,
        primaryVariable.measurement,
        primaryVariable.unit,
        { excludeLogId: row.id },
      ),
    };
  }, [
    isPremium,
    row.activity?.id,
    row.variables,
    row.selectedIndices,
    row.id,
    pastPerformanceLogs,
    historyReady,
    historyLoadFailed,
  ]);

  const autoFlaggedPbRef = useRef(false);
  const pbDismissedRef = useRef(false);
  const pbEvalKeyRef = useRef("");
  const pbEvalKey = personalBestEvaluation
    ? `${row.activity?.id}|${personalBestEvaluation.variableName}|${personalBestEvaluation.isNewPersonalBest}|${personalBestEvaluation.previousBest}`
    : "";

  useEffect(() => {
    if (pbEvalKey !== pbEvalKeyRef.current) {
      if (
        pbEvalKey.split("|").slice(0, 2).join("|") !==
        pbEvalKeyRef.current.split("|").slice(0, 2).join("|")
      ) {
        pbDismissedRef.current = false;
      }
      pbEvalKeyRef.current = pbEvalKey;
    }
  }, [pbEvalKey]);

  useEffect(() => {
    if (!personalBestEvaluation) return;
    if (personalBestEvaluation.isNewPersonalBest) {
      if (!pbDismissedRef.current && !row.isPB) {
        onChange({ ...row, isPB: true });
        autoFlaggedPbRef.current = true;
      }
    } else if (autoFlaggedPbRef.current && row.isPB) {
      onChange({ ...row, isPB: false });
      autoFlaggedPbRef.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [personalBestEvaluation?.isNewPersonalBest, pbEvalKey]);

  return (
    <div className="rounded-md border border-base-300 bg-base-200/30 p-4 space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-xs font-medium text-base-content/50 font-grotesk">
          Result {index + 1}
        </p>
        <button
          type="button"
          onClick={onRemove}
          aria-label="Remove result"
          className="w-8 h-8 rounded-full flex items-center justify-center text-base-content/50 hover:bg-base-200 hover:text-error"
        >
          <HiTrash className="w-4 h-4" />
        </button>
      </div>

      <ActivityResultEditor
        compact
        activityTypes={activityTypes}
        loadingActivityTypes={loadingActivityTypes}
        selectedActivity={row.activity}
        variables={row.variables}
        selectedIndices={row.selectedIndices}
        targets={row.targets}
        onSelectActivity={(activity) => {
          if (!activity) {
            onChange({
              ...row,
              activity: null,
              variables: [],
              selectedIndices: new Set(),
            });
            return;
          }
          const variables = initialVariablesFor(activity);
          onChange({
            ...row,
            activity,
            variables,
            selectedIndices: new Set(variables.map((_, i) => i)),
          });
        }}
        onVariablesChange={(variables) => onChange({ ...row, variables })}
        onToggleIndex={(i) => {
          const next = new Set(row.selectedIndices);
          if (next.has(i)) next.delete(i);
          else next.add(i);
          onChange({ ...row, selectedIndices: next });
        }}
        onActivityCreated={async (id) => {
          const created = await onActivityCreated(id);
          if (created) {
            const variables = initialVariablesFor(created);
            onChange({
              ...row,
              activity: created,
              variables,
              selectedIndices: new Set(variables.map((_, i) => i)),
            });
          }
        }}
      />

      {/* Target vs actual line (session target values) */}
      {row.targets && Object.keys(row.targets).length > 0 && (
        <div className="space-y-0.5">
          {Object.entries(row.targets).map(([name, t]) => {
            const variable = row.variables.find(
              (v) => v.name.toLowerCase() === name,
            );
            const measured =
              variable && variable.measurement != null
                ? formatMeasurementWithUnit(variable.measurement, variable.unit, variable.name)
                : null;
            return (
              <p
                key={name}
                className="text-sm font-geist tabular-nums text-base-content/80"
              >
                <span className="capitalize">{name}</span> target{" "}
                <span className="font-semibold">
                  {formatMeasurementWithUnit(t.goal, t.unit, name)}
                </span>
                {measured && (
                  <>
                    {" · "}You <span className="font-semibold">{measured}</span>
                  </>
                )}
              </p>
            );
          })}
        </div>
      )}

      {/* PB / SB pills + rating */}
      <div className="flex flex-wrap items-center gap-2">
        <Pill
          active={row.isPB}
          onClick={() => {
            const next = !row.isPB;
            if (!next) {
              pbDismissedRef.current = true;
              autoFlaggedPbRef.current = false;
            } else {
              pbDismissedRef.current = false;
            }
            onChange({ ...row, isPB: next });
          }}
        >
          PB
        </Pill>
        <Pill active={row.isSB} onClick={() => onChange({ ...row, isSB: !row.isSB })}>
          SB
        </Pill>
      </div>

      {personalBestEvaluation && (
        <div
          className={`rounded-md p-3 text-sm border ${
            personalBestEvaluation.isNewPersonalBest
              ? "bg-primary/10 border-primary/30 text-primary"
              : "bg-base-100 border-base-300 text-base-content/80"
          }`}
        >
          <p className="font-bold font-sans">
            {personalBestEvaluation.isNewPersonalBest
              ? `New Personal Best for ${personalBestEvaluation.variableName}!`
              : "PB Predictor"}
          </p>
          {personalBestEvaluation.previousBest !== null && (
            <p className="text-xs mt-1 opacity-80 font-sans">
              Previous best: {personalBestEvaluation.previousBest.toFixed(2)}
            </p>
          )}
          {personalBestEvaluation.predictedNextBest !== null && (
            <p className="text-xs mt-1 opacity-80 font-sans">
              Based on your recent trend, your next{" "}
              {personalBestEvaluation.variableName.toLowerCase()} is projected
              around {personalBestEvaluation.predictedNextBest.toFixed(2)}.
            </p>
          )}
        </div>
      )}
      {!isPremium && hasMeasurementToEvaluate && (
        <div className="flex items-center gap-2 rounded-md p-3 text-xs bg-base-100 border border-base-300 text-base-content/50">
          <HiLockClosed className="w-3.5 h-3.5 flex-shrink-0" />
          <span className="font-sans">
            PB Predictor is a Premium feature — automatically detect personal
            bests and see your projected next result.
          </span>
        </div>
      )}

      <div>
        <div className="flex justify-between items-center mb-1">
          <p className="text-xs font-medium text-base-content/80">
            Performance rating
          </p>
          <span
            className="font-geist text-sm font-bold tabular-nums"
            style={{ color: ratingColor(row.performanceExpectation) }}
          >
            {row.performanceExpectation}/10
          </span>
        </div>
        <input
          type="range"
          min="1"
          max="10"
          step="1"
          value={row.performanceExpectation}
          onChange={(e) =>
            onChange({ ...row, performanceExpectation: Number(e.target.value) })
          }
          className="tl-slider w-full"
          style={{ background: ratingBackground(row.performanceExpectation) }}
        />
      </div>

      <textarea
        value={row.notes}
        onChange={(e) => onChange({ ...row, notes: e.target.value })}
        placeholder="Notes about this result (optional)"
        rows={2}
        className="w-full px-4 py-3 rounded-md bg-base-100 border border-base-300 focus:border-base-300 resize-none focus:outline-none focus:ring-0 text-[16px] placeholder:text-base-content/40"
      />
    </div>
  );
};

export default ResultRowEditor;
